import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { confirmAlert } from "react-confirm-alert";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faPlus } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import Button from "./Button";
import { getUsersForCompany } from "../store/users/actions";
import {
  addAndRemoveDistributees,
  resendSDSNotification,
} from "../store/sds/actions";
import { loadContractor } from "../store/contractors/actions";
import {
  getDistributeesLoading,
  getDistributeesError,
} from "../store/sds/selectors";
import { getContractorById } from "../store/contractors/selectors";
import { distributeDark, distributeLight } from "../styles/colors";
import Modal from "react-modal";
import OpenDispute from "./OpenDispute";
import CloseDispute from "./CloseDispute";
import ViewDispute from "./ViewDispute";

const DistributeSurround = styled.div`
  margin-top: 20px;
  border: 1px solid ${distributeDark};
  background-color: ${distributeLight};
  padding: 10px;

  h4 {
    margin: 0px !important;
    text-align: left !important;
    font-size: 16px !important;
  }
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  margin-right: 10px;
`;

const Table = styled.table`
  width: 100%;
  margin-bottom: 20px;

  thead {
    td {
      background-color: ${distributeDark};
      color: #fff;
      text-align: center;
      font-weight: bold;
    }
  }

  th {
    font-weight: bold;
    color: ${distributeDark};
    text-align: left;
    background-color: #fff;
    padding: 4px;
  }

  td {
    padding: 4px;
    background-color: #fff;
  }
`;

const Selector = styled.select`
  width: 100%;
  font-size: 16px;
  outline: none;
  border: 1px solid #ccc;
  margin-right: 10px;
`;

const canReceiveSDS = (user) =>
  user &&
  user.roles &&
  user.roles.find((role) => role.name === "user" || role.name === "sds");

function DistributePanel({ engagement, contractorId, noSDS, reload }) {
  const dispatch = useDispatch();
  const loading = useSelector(getDistributeesLoading);
  const error = useSelector(getDistributeesError);
  const distributees = engagement && engagement.distributees;
  const hasDistributees = distributees && distributees.length > 0;
  const hasContractor =
    hasDistributees &&
    distributees.length &&
    distributees.find((distributee) => distributee.type === "CONTRACTOR");
  const [newUsers, setNewUsers] = useState([0]);
  const [remove, setRemove] = useState([]);
  const contractor = useSelector((state) =>
    getContractorById(state, Number(contractorId))
  );
  const recruiters =
    distributees &&
    distributees.filter((distributee) => distributee.type === "RECRUITER");
  const activeRecruiters = (recruiters || []).filter(
    (recruiter) => !remove.includes(recruiter.id)
  );
  const [supplyChainUsers, setSupplyChainUsers] = useState([]);
  const [loadingSupplyChainUsers, setLoadingSupplyChainUsers] = useState(false);
  const contractorCompanyId = contractor && contractor.company_id;
  const contractorDistributee =
    distributees &&
    distributees.find((distributee) => distributee.type === "CONTRACTOR");
  const [resendLoading, setResendLoading] = useState(0);

  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [disputePublicId, setDisputePublicId] = useState(null);

  const [closeDisputeModalOpen, setCloseDisputeModalOpen] = useState(null);
  const [viewDisputeModalOpen, setViewDisputeModalOpen] = useState(null);
  const [disputeDistributee, setDisputeDistributee] = useState(null);

  const [saving, setSaving] = useState(false);
  const buttonDisabled = loadingSupplyChainUsers || saving || loading;

  useEffect(() => {
    let isMounted = true;

    if (contractorCompanyId) {
      const loadSupplyChainUsers = async () => {
        setLoadingSupplyChainUsers(true);

        try {
          const users = await dispatch(
            getUsersForCompany(contractorCompanyId, "all", false)
          );

          if (isMounted) {
            setSupplyChainUsers((users || []).filter(canReceiveSDS));
          }
        } finally {
          if (isMounted) {
            setLoadingSupplyChainUsers(false);
          }
        }
      };

      loadSupplyChainUsers();
    }

    return () => {
      isMounted = false;
    };
  }, [contractorCompanyId, dispatch]);

  const reloadEngagement = () =>
    dispatch(
      loadContractor(
        contractorId,
        contractor && contractor.company_id,
        engagement && engagement.id
      )
    );

  const doDistribute = () => {
    const options = {
      title: "Are you sure?",
      message: [
        <p>
          By continuing the system will email the parties you have identified
          with the Status Determination Statement.
        </p>,
        <p>Do you wish to continue?</p>,
      ],
      buttons: [
        {
          label: "Continue",
          onClick: async () => {
            let newToSend = [];
            if (!hasContractor || hasContractor.status === "READY_REISSUE") {
              newToSend.push({
                id: hasContractor && hasContractor.id,
                name: `${contractor.first_name} ${contractor.last_name}`,
                email: contractor.email,
                engagement_id: engagement.id,
                type: "CONTRACTOR",
              });
            }
            activeRecruiters.forEach((recruiter) => {
              newToSend.push({
                id: recruiter.id,
                name: recruiter.name,
                email: recruiter.email,
                engagement_id: engagement.id,
                type: "RECRUITER",
              });
            });
            newUsers
              .filter((newUser) => newUser !== 0)
              .forEach((newUserId) => {
                const user = supplyChainUsers.find(
                  (supplyChainUser) =>
                    Number(supplyChainUser.id) === Number(newUserId)
                );

                if (
                  user &&
                  !newToSend.find((distributee) => distributee.email === user.email)
                ) {
                  newToSend.push({
                    name: `${user.first_name} ${user.last_name}`,
                    email: user.email,
                    engagement_id: engagement.id,
                    type: "RECRUITER",
                  });
                }
              });
            setSaving(true);
            try {
              await dispatch(
                addAndRemoveDistributees(
                  newToSend,
                  remove,
                  contractorId,
                  contractor && contractor.company_id,
                  engagement && engagement.id
                )
              );
              setNewUsers([0]);
              setRemove([]);
            } finally {
              setSaving(false);
            }
          },
        },
        {
          label: "Cancel",
        },
      ],
    };

    confirmAlert(options);
  };

  const removeNew = (idx) =>
    setNewUsers(newUsers.filter((newUser, index) => index !== idx));

  const addNew = () => setNewUsers([...newUsers, 0]);

  const removeExisting = (id) => setRemove([...remove, id]);

  const doResend = async (id) => {
    setResendLoading(id);
    await dispatch(resendSDSNotification(id));
    setResendLoading(0);
    reloadEngagement();
  };

  const doCancel = () => {
    const options = {
      title: "Are you sure?",
      message: [
        <p>
          By continuing you will remove any signatories already made and revoke
          access for those not yet complete.
        </p>,
        <p>This cannot be undone</p>,
        <p>Do you wish to continue?</p>,
      ],
      buttons: [
        {
          label: "Continue",
          onClick: () => {
            dispatch(
              addAndRemoveDistributees(
                [],
                [
                  ...(recruiters || []).map((recruiter) => recruiter.id),
                  contractorDistributee.id,
                ],
                contractorId,
                contractor && contractor.company_id,
                engagement && engagement.id
              )
            );

            setNewUsers([0]);
            setRemove([]);
          },
        },
        {
          label: "Cancel",
        },
      ],
    };

    confirmAlert(options);
  };

  const openModal = (publicId) => {
    setDisputePublicId(publicId);
    setDisputeModalOpen(true);
  };

  const openCloseDisputeModal = (distributee) => {
    setDisputeDistributee(distributee);
    setCloseDisputeModalOpen(true);
  };
  const openViewDisputeModal = (distributee) => {
    setDisputeDistributee(distributee);
    setViewDisputeModalOpen(true);
  };

  const closeModal = () => {
    setDisputeModalOpen(false);
    reloadEngagement();
  };

  const reEnableSDS = () => {
    const options = {
      title: "Are you sure?",
      message: [
        <p>
          By continuing, you will re-enable the SDS functionality for this
          assessment. This will move this assessment from 'Complete' to 'Waiting
          SDS Distribution' and is not reversible.
        </p>,
      ],
      buttons: [
        {
          label: "Continue",
          onClick: async () => {
            reload();
          },
        },
        {
          label: "Cancel",
        },
      ],
    };

    confirmAlert(options);
  };

  const customStyles = {
    overlay: {
      zIndex: 100000000000,
    },
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
    },
  };

  return (
    <DistributeSurround>
      <Modal
        isOpen={disputeModalOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Open Dispute"
      >
        <OpenDispute
          onClose={closeModal}
          publicId={disputePublicId}
        ></OpenDispute>
      </Modal>
      <Modal
        isOpen={closeDisputeModalOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Resolve Dispute"
      >
        <CloseDispute
          onClose={closeModal}
          distributee={disputeDistributee}
        ></CloseDispute>
      </Modal>
      <Modal
        isOpen={viewDisputeModalOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="View Dispute"
      >
        <ViewDispute
          onClose={closeModal}
          distributee={disputeDistributee}
        ></ViewDispute>
      </Modal>
      <h4>
        <StyledFontAwesomeIcon icon={faPaperPlane} />
        Distribute
      </h4>
      {noSDS && (
        <>
          <p>
            As this is a reassessment, an SDS will not be distributed by
            default. You are able to re-enable the SDS functionality by pressing
            the button below. This action is not reversable.
          </p>
          <Button noFullWidth={true} type="distribute" onClick={reEnableSDS}>
            Re-enable SDS Distribution
          </Button>
        </>
      )}
      {!noSDS && (
        <>
          {!hasDistributees && (
            <p>
              Add parties to send the Status Determination Statement to. They
              will be asked to acknowledge receipt and offered the chance to
              dispute the outcome.
            </p>
          )}
          {hasDistributees && <p>The SDS has been distributed</p>}
          {error && <p>There was a problem saving the SDS distribution.</p>}
          <Table>
            <thead>
              <tr>
                <td colspan="4">
                  {distributees &&
                    "The following people have been sent the SDS"}
                  {!distributees && "Send SDS to relevant parties"}
                </td>
              </tr>
              <tr>
                <th width="180">Party</th>
                <th width="180">Name</th>
                <th width="120">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Contractor</td>
                <td>
                  {contractor &&
                    `${contractor.first_name} ${contractor.last_name}`}
                </td>
                <td>
                  {!contractorDistributee && "Not sent"}
                  {contractorDistributee &&
                    contractorDistributee.status === "READY_TO_SEND" &&
                    "Ready to resend"}
                  {contractorDistributee &&
                    contractorDistributee.status === "ACCEPTED" &&
                    `Acknowledged on ${moment(
                      contractorDistributee.datetime
                    ).format("DD/MM/YYYY")}`}
                  {contractorDistributee &&
                    contractorDistributee.status !== "ACCEPTED" &&
                    contractorDistributee.status !== "IN_DISPUTE" &&
                    contractorDistributee.status !== "READY_TO_SEND" &&
                    "Sent"}
                  {contractorDistributee &&
                    contractorDistributee.status === "IN_DISPUTE" &&
                    "Disputed"}
                </td>
                <td>
                  {contractorDistributee &&
                    contractorDistributee.status !== "ACCEPTED" && (
                      <Button
                        type="distribute"
                        size="reduced"
                        loading={resendLoading === contractorDistributee.id}
                        noFullWidth={true}
                        onClick={() => doResend(contractorDistributee.id)}
                      >
                        Resend
                      </Button>
                    )}

                  {contractorDistributee &&
                    contractorDistributee.status !== "IN_DISPUTE" &&
                    !contractorDistributee.dispute_complete && (
                      <Button
                        type="appeal"
                        size="reduced"
                        noFullWidth={true}
                        onClick={() =>
                          openModal(contractorDistributee.public_id)
                        }
                      >
                        Open Dispute
                      </Button>
                    )}

                  {contractorDistributee &&
                    contractorDistributee.status === "IN_DISPUTE" &&
                    !contractorDistributee.dispute_complete && (
                      <Button
                        type="appeal"
                        size="reduced"
                        noFullWidth={true}
                        onClick={() =>
                          openCloseDisputeModal(contractorDistributee)
                        }
                      >
                        Resolve Dispute
                      </Button>
                    )}
                  {contractorDistributee &&
                    contractorDistributee.dispute_complete && (
                      <Button
                        type="appeal"
                        size="reduced"
                        noFullWidth={true}
                        onClick={() =>
                          openViewDisputeModal(contractorDistributee)
                        }
                      >
                        View Dispute
                      </Button>
                    )}
                </td>
              </tr>
              {recruiters &&
                recruiters
                  .filter((recruiter) => !remove.includes(recruiter.id))
                  .map((recruiter) => (
                    <tr>
                      <td>Supply Chain Member</td>
                      <td>{recruiter.name}</td>
                      <td>
                        {recruiter.status === "READY_TO_SEND" &&
                          "Ready to resend"}
                        {recruiter.status === "ACCEPTED"
                          ? `Acknowledged on ${moment(
                              recruiter.datetime
                            ).format("DD/MM/YYYY")}`
                          : recruiter.status === "IN_DISPUTE"
                          ? "Disputed"
                          : recruiter.status !== "READY_TO_SEND" && "Sent"}
                      </td>
                      <td>
                        {recruiter.status !== "ACCEPTED" && (
                          <Button
                            type="distribute"
                            size="reduced"
                            noFullWidth={true}
                            loading={resendLoading === recruiter.id}
                            onClick={() => doResend(recruiter.id)}
                          >
                            Resend
                          </Button>
                        )}
                        {"  "}
                        <Button
                          type="danger"
                          size="reduced"
                          noFullWidth={true}
                          onClick={() => removeExisting(recruiter.id)}
                        >
                          Remove
                        </Button>
                        {"  "}
                        {recruiter.status !== "IN_DISPUTE" &&
                          !recruiter.dispute_complete && (
                            <Button
                              type="appeal"
                              size="reduced"
                              noFullWidth={true}
                              onClick={() => openModal(recruiter.public_id)}
                            >
                              Open Dispute
                            </Button>
                          )}
                        {"  "}
                        {recruiter.status === "IN_DISPUTE" &&
                          !recruiter.dispute_complete && (
                            <Button
                              type="appeal"
                              size="reduced"
                              noFullWidth={true}
                              onClick={() => openCloseDisputeModal(recruiter)}
                            >
                              Resolve Dispute
                            </Button>
                          )}
                        {recruiter.dispute_complete && (
                          <Button
                            type="appeal"
                            size="reduced"
                            noFullWidth={true}
                            onClick={() => openViewDisputeModal(recruiter)}
                          >
                            View Dispute
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
              {newUsers &&
                newUsers.map((newUser, indx) => (
                  <tr>
                    <td>Supply Chain Member</td>
                    <td>
                      <Selector
                        value={newUser}
                        onChange={(e) => {
                          //Add to the new array
                          const copiedUsers = [...newUsers];
                          copiedUsers[indx] = Number(e.target.value);
                          setNewUsers(copiedUsers);
                        }}
                      >
                        <option value="0">Choose</option>
                        {loadingSupplyChainUsers && (
                          <option value="0" disabled>
                            Loading supply chain users...
                          </option>
                        )}
                        {supplyChainUsers
                          .filter((user) => {
                            const selectedInOtherRows = newUsers
                              .filter((selectedUser, selectedIndex) => {
                                return selectedIndex !== indx && selectedUser !== 0;
                              })
                              .map((selectedUser) => Number(selectedUser));
                            const alreadyDistributed = activeRecruiters.find(
                              (recruiter) => recruiter.email === user.email
                            );

                            return (
                              Number(user.id) === Number(newUser) ||
                              (!selectedInOtherRows.includes(Number(user.id)) &&
                                !alreadyDistributed)
                            );
                          })
                          .map((user) => {
                            return (
                              <option value={user.id}>{`${user.first_name} ${
                                user.last_name
                              } ${
                                user.external_company_name
                                  ? `(${user.external_company_name})`
                                  : ``
                              }`}</option>
                            );
                          })}
                      </Selector>
                    </td>
                    <td>Not sent</td>
                    <td>
                      {indx !== 0 && (
                        <Button
                          type="distribute"
                          size="reduced"
                          onClick={() => removeNew(indx)}
                        >
                          Remove
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              <tr>
                <td colspan="4">
                  <Button
                    type="distribute"
                    noFullWidth={true}
                    size="reduced"
                    onClick={() => addNew()}
                    disabled={newUsers[newUsers.length - 1] === 0}
                  >
                    <StyledFontAwesomeIcon icon={faPlus} />
                    Add New
                  </Button>
                </td>
              </tr>
            </tbody>
          </Table>
          <Button
            type="distribute"
            disabled={buttonDisabled}
            noFullWidth={true}
            loading={loading || saving}
            onClick={doDistribute}
          >
            Save
          </Button>{" "}
          <Button
            type="distribute"
            disabled={!contractorDistributee}
            noFullWidth={true}
            loading={loading}
            onClick={doCancel}
          >
            Cancel
          </Button>
        </>
      )}
    </DistributeSurround>
  );
}

export default DistributePanel;
